import { Search } from "@outschool/business-rules";
import { EVENT_SOURCE } from "@outschool/experiments-shared";
import {
  ListingsPromoBannerButtonFragment,
  ListingsPromoBannerFragment,
  SearchUseCase,
} from "@outschool/gql-frontend-generated";
import { useLocalStorageState } from "@outschool/local-storage";
import { usePageContext } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import { useSearchQuery } from "@outschool/ui-components-website";
import {
  useContentfulExperiment,
  useServerExperimentContext,
} from "@outschool/ui-experiments";
import ldIsEmpty from "lodash/isEmpty";
import ldIsNil from "lodash/isNil";
import ldOmitBy from "lodash/omitBy";
import React from "react";

import { mapContentfulFiltersToSearchFilters } from "../SearchFilters";
import { BannerType } from "./BannerType";
import { useFetchListingPromoBanners } from "./graphql";
import ListingsPromoBannerVisualComponent from "./ListingsPromoBannerComponent";

type ListingsPromoBannerProps = {
  banner: ListingsPromoBannerFragment;
  onMuteBanner(): void;
};

function ListingsPromoBanner({
  banner,
  onMuteBanner,
}: ListingsPromoBannerProps) {
  const { currentUserHasLoaded } = useSession();
  const { pageImpressionId } = usePageContext();
  const { loggedOutUserExperimentUid } = useServerExperimentContext();

  const [buttonSelected, setButtonSelected] = React.useState(
    banner.buttonsCollection?.items?.[0],
  );

  const buttons: ListingsPromoBannerButtonFragment[] = React.useMemo(() => {
    return (
      banner.buttonsCollection?.items?.filter(
        (
          b: ListingsPromoBannerButtonFragment | null,
        ): b is ListingsPromoBannerButtonFragment => !!b,
      ) ?? []
    );
  }, [banner.buttonsCollection?.items]);

  const searchFilters = React.useMemo(
    () =>
      mapContentfulFiltersToSearchFilters(buttonSelected?.buttonSearchFilters),
    [buttonSelected?.buttonSearchFilters],
  );

  const { loading: listingsLoading, exactListings } = useSearchQuery(
    {
      variables: {
        searchFilters,
        limit: 4,
        offset: 0,
        skipRating: false,
        withRecentReviews: false,
        includeMatchingSections: false,
        anonymousId: loggedOutUserExperimentUid,
        pageImpressionId,
        isFuzzySearchEnabled: false,
        pageLocation: "LISTINGS_PROMO_BANNER",
        useCase: SearchUseCase.Discover,
      },
      skip: !buttonSelected || !currentUserHasLoaded,
    },
    true,
  );

  const handleButtonClick = React.useCallback(
    (buttonItem: ListingsPromoBannerButtonFragment) => {
      if (buttonItem.buttonText !== buttonSelected?.buttonText) {
        setButtonSelected(buttonItem);
      }
    },
    [setButtonSelected, buttonSelected?.buttonText],
  );

  return (
    <ListingsPromoBannerVisualComponent
      banner={banner}
      buttons={buttons}
      onMuteBanner={onMuteBanner}
      onButtonClick={handleButtonClick}
      buttonSelected={buttonSelected}
      listingsLoading={listingsLoading}
      listings={exactListings}
    />
  );
}

type ListingsPromoBannerWrapperProps = {
  bannerType: BannerType;
  eventSource: EVENT_SOURCE;
  queryParams: Record<string, any>;
};

export default function ListingsPromoBannerWrapperFetchFromContentful({
  bannerType,
  eventSource,
  queryParams,
}: ListingsPromoBannerWrapperProps) {
  // Only display the banner when the user has no search filters applied
  const shouldShowBanner = React.useMemo(
    () => ldIsEmpty(ldOmitBy(Search.fromQuery(queryParams), ldIsNil)),
    [queryParams],
  );

  const { banners } = useFetchListingPromoBanners({
    bannerType,
    skip: !shouldShowBanner,
  });
  const hasBanners = banners.length > 0;
  const shouldIncludeControlGroup = banners?.some(b => !!b.includeControlGroup);

  const { variantAssigned, trigger } = useContentfulExperiment({
    variants: banners,
    eventSource,
    shouldIncludeControlGroup,
  });

  const banner: ListingsPromoBannerFragment | undefined = React.useMemo(
    () => banners.find(b => b.name === variantAssigned),
    [banners, variantAssigned],
  );

  const [bannerIsMuted, setBannerIsMuted] = useLocalStorageState(
    `os-listings-promo-banner-${banner?.name}`,
    false,
  );

  React.useEffect(() => {
    if (shouldShowBanner && hasBanners && !bannerIsMuted) {
      trigger();
    }
  }, [shouldShowBanner, trigger, bannerIsMuted, hasBanners]);

  if (!shouldShowBanner || !banner || bannerIsMuted) {
    return null;
  }

  return (
    <ListingsPromoBanner
      banner={banner}
      onMuteBanner={() => setBannerIsMuted(true)}
    />
  );
}
